/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { isEqual } from 'lodash';

import { Accordion } from 'carbon-react/lib/components/accordion';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import { Option, Select } from 'carbon-react/lib/components/select';

import { ForecastListItem } from '@futrli-sdk/predict';
import { FormattedMessage, useIntl } from '@futrli/localisation';

import { DataField } from '../../types';
import { useReportBuilderContext } from '../../report-builder.context';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './dataset-comparison-settings.scss';

interface Props {
  field: DataField;
}

export const DatasetComparisonSettings = ({ field }: Props) => {
  const intl = useIntl();
  const {
    getSelectedDatasets,
    forecasts,
    getSelectedComparisons,
    setSelectedComparisons,
    isTemplate,
    isWorkspaceTemplate
  } = useReportBuilderContext();
  const { actualForecasts, dummyTemplateForecasts } = useI18nConstants();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const selectedComparisons = getSelectedComparisons(field.ref);
  const [comparisons, setComparisons] = useState<string[][]>(selectedComparisons);
  const selectedForecasts = getSelectedDatasets();
  const selectedDatasets = selectedForecasts
    .map(
      f =>
        forecasts.find(fs => fs.id === f) ||
        actualForecasts.find(fs => fs.id === f) ||
        (isFutrliTemplate && dummyTemplateForecasts.find(fs => fs.id === f))
    )
    .filter(e => e !== undefined) as ForecastListItem[];

  useEffect(() => {
    if (selectedForecasts.length > 1 && !selectedComparisons.length) {
      setComparisons([[selectedForecasts[0], selectedForecasts[1]]]);
    }
  }, [selectedForecasts, selectedComparisons]);

  useEffect(() => {
    if (!isEqual(comparisons, selectedComparisons)) {
      setSelectedComparisons(field.ref, comparisons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons]);

  useEffect(() => {
    if (!isEqual(comparisons, selectedComparisons)) {
      setComparisons(selectedComparisons.length ? selectedComparisons : [['', '']]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComparisons]);

  const handleComparisonChange = (dsID: string, cmpIndex: number, dsIndex: number) => {
    const cmpCopy = [...comparisons];
    cmpCopy[cmpIndex] =
      dsIndex === 0
        ? [dsID, cmpCopy[cmpIndex][1] === dsID ? cmpCopy[cmpIndex][0] : cmpCopy[cmpIndex][1]]
        : [cmpCopy[cmpIndex][0] === dsID ? cmpCopy[cmpIndex][1] : cmpCopy[cmpIndex][0], dsID];

    setComparisons(cmpCopy);
  };

  const handleAddAnother = () => {
    setComparisons([...comparisons, ['', '']]);
  };

  const handleDeleteComparison = (index: number) => {
    const compCopy = [...comparisons];
    compCopy.splice(index, 1);
    setComparisons(compCopy);
  };

  const getAvailableOptions = (compIndex: number, dsIndex: number) => {
    let options: ForecastListItem[] = selectedDatasets;
    if (comparisons[compIndex][(dsIndex + 1) % 2]) {
      comparisons.forEach((cmp, i) => {
        if (i !== compIndex) {
          if (cmp[0] === comparisons[compIndex][(dsIndex + 1) % 2]) {
            options = options.filter(ds => ds.id !== cmp[1]);
          }
          if (cmp[1] === comparisons[compIndex][(dsIndex + 1) % 2]) {
            options = options.filter(ds => ds.id !== cmp[0]);
          }
        }
      });
    } else {
      const dsUsage = selectedDatasets.map(ds => ({ id: ds.id, u: 0 }));
      dsUsage.forEach(ds =>
        comparisons.forEach((cmp, i) => {
          if (i !== compIndex && (cmp[0] === ds.id || cmp[1] === ds.id)) {
            ds.u += 1;
          }
        })
      );
      options = selectedDatasets.filter(
        ds => (dsUsage.find(d => d.id === ds.id)?.u || 0) < selectedDatasets.length - 1
      );
    }
    return options.filter(ds => !!ds);
  };

  return selectedDatasets.length > 1 ? (
    <div className={styles.expandPanelContainerCarbon}>
      <Accordion
        borders="full"
        title={<span className={clsx(styles.panelSummary)}>{field.name}</span>}
        headerSpacing={{ p: 0 }}
      >
        <div className={clsx(styles.panelDetails)}>
          {comparisons.map((cmp, i) => (
            <div
              key={`${cmp}-${i}`}
              className={clsx(styles.comparisonBlock, i === comparisons.length - 1 && styles.isLast)}
            >
              {comparisons.length > 1 && (
                <div className={clsx(styles.comparisonHeader)}>
                  <span>
                    <FormattedMessage id="reporting.variance" defaultMessage="Variance" /> {i + 1}
                  </span>
                  <Button onClick={() => handleDeleteComparison(i)} buttonType="tertiary" size="small">
                    <Icon type="cross" />
                  </Button>
                </div>
              )}
              <Select
                id={`primary-dataset-${i}`}
                autoComplete="off"
                onChange={e => handleComparisonChange(e.target.value, i, 0)}
                value={cmp[0]}
                size="small"
                label={intl.formatMessage({ id: 'reporting.primary_dataset', defaultMessage: 'Primary Dataset' })}
                mb={1}
              >
                <Option value="" text="" />
                {getAvailableOptions(i, 0).map(ds => (
                  <Option key={ds.id} value={ds.id} text={ds.name} />
                ))}
              </Select>
              <Select
                id={`comparative-dataset-${i}`}
                autoComplete="off"
                onChange={e => handleComparisonChange(e.target.value, i, 1)}
                value={cmp[1]}
                size="small"
                label={intl.formatMessage({
                  id: 'reporting.comparative_dataset',
                  defaultMessage: 'Comparative dataset'
                })}
                mb={1}
              >
                <Option value="" text="" />
                {getAvailableOptions(i, 1).map(ds => (
                  <Option key={`${cmp}-${i}-${ds.id}`} value={ds.id} text={ds.name} />
                ))}
              </Select>
            </div>
          ))}
          {selectedDatasets.length > 2 && comparisons.length < (selectedDatasets.length === 3 ? 3 : 6) && (
            <Button className={styles.addButton} onClick={handleAddAnother} size="small">
              <FormattedMessage id="reporting.add_another" defaultMessage="Add another" />
            </Button>
          )}
        </div>
      </Accordion>
    </div>
  ) : null;
};
